/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Image, Link } from '@ta-interaktiv/contentbricks'

import {
  Timeline,
  Textbox,
  History,
  Separator
} from '../timeline/main.component.jsx'

// TODO Set the correct article ID here
const ARTICLE_ID = '210764730894'
// Idee:
// https://www.nytimes.com/interactive/2020/06/10/world/coronavirus-history.html

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>Soviel könnte Corona den Kanton Zürich kosten</h1>
            <h2>
              Das Coronavirus wütet in Zürichs Buchhaltung. Finanzspritzen für
              darbende Unternehmen, Steuerausfälle, aber auch auf den ersten
              Blick kuriose Kosten belasten das Budget. Eine unvollständige
              Zusammenstellung.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>Simon Huwiler</div>
            <div className='ta updated'>Aktualisiert am 2. Juli 2020</div>
          </div>
          <div>
            Die erste Welle ist vorüber. Das grosse Aufarbeiten beginnt. Welchen
            finanziellen Schaden hat das Coronavirus bisher angerichtet? Erste
            Hinweise liefern einzelne Behörden, Regierungsratsbeschlüsse und die
            Wissenschaft. Diese Zeitung hat die bereits bekannten Zahlen
            zusammengetragen. Entstanden ist eine unvollständige Liste mit
            vielen Unbekannten.
            <div className='box-beige'>
              <b>Bevor es losgeht</b>
              <br />
              Wir unterteilen die Ausgaben in drei Kategorien:
              <ol>
                <li>
                  <b>Schnelle Hilfe</b> sind jene finanziellen Mittel, die
                  sofort zur Verfügung gestellt wurden.
                </li>
                <li>
                  <b>Ausfälle und Mindereinnahmen</b> belasten das Budget
                  bereits jetzt oder werden sich noch auf den Finanzhaushalt
                  auswirken.
                </li>
                <li>
                  <b>Garantien und Bürgschaften</b> garantieren Unternehmen
                  schnelle Darlehen. Diese können das Budget belasten, müssen
                  aber nicht.
                </li>
              </ol>
            </div>
            <div className='legend'>
              <div className='inner'>
                <div className='floater corona'>
                  <span className='dot' />
                  <span className='text'>Ausgaben wegen Corona (summiert)</span>
                </div>
                <div className='floater vergleich'>
                  <span className='dot' />
                  <span className='text'>Vergleichsausgaben</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Timeline from={0} to={1800} step={1} titleOffset={50} linespace={25}>
        {/* -- HISTORY -- */}
        <History pos={11.5} align='left' value='11.5 Mio.' hideOnMobile>
          Budget Züri-Fäscht 2019
        </History>

        <History pos={24.4} align='right' value='24.4 Mio.' hideOnMobile>
          Ausgaben des FCZ (2018)
        </History>

        <History pos={38.8} align='left' value='38.8 Mio.'>
          Jährliche Betriebs&shy;kosten städtischer Schwimmbäder
        </History>

        <History pos={75} align='right' value='75 Mio.'>
          Prognostizierte Kosten der ZKB-Seilbahn
        </History>

        <History pos={80.3} align='left' value='80.3 Mio.'>
          Jährlicher Förderbeitrag an das Opernhaus
        </History>

        <History pos={105.3} align='right' value='105.2 Mio.'>
          Neues Hardturm&shy;stadion
        </History>

        <History pos={300} align='right' value='300 Mio.'>
          Triemli-Bettenhaus
        </History>

        <History pos={380} align='left' value='380 Mio.'>
          Baukosten Prime Tower
        </History>

        <History pos={512} align='left' value='512 Mio.'>
          Gewinn Lindt & Sprüngli (2019)
        </History>

        <History pos={739.1} align='right' value='739 Mio.'>
          Neues Polizei- und Justizgebäude
        </History>

        <History pos={940} align='left' value='940 Mio.'>
          Ausbau Nordumfahrung
        </History>

        <History pos={992.2} align='right' value='992.2 Mio.'>
          Betriebs&shy;aufwand des ZVV (2018)
        </History>

        <History pos={1146} align='left' value='1146 Mio.'>
          Umsatz Digitec Galaxus (2019)
        </History>

        <History pos={1308} align='left' value='1308 Mio.'>
          Jahresbudget der ETH Zürich (2019)
        </History>

        <History pos={1626} align='left' value='1626 Mio.'>
          Alle Ausgaben der Stadt Winterthur (2019)
        </History>

        <History pos={1900} align='right' value='1900 Mio.'>
          Neubeschaffung Zug FV-Dosto
        </History>

        {/* -- SOFORTHILFE -- */}
        <Separator pos={0} title='Schnelle Hilfe' />

        <Textbox value={2.5} accurate align='left'>
          In Windeseile hat der Regierungsrat ein Massnahmenpaket verabschiedet,
          um gebeutelte Branchen zu unterstützen. Der <b>Tourismus</b> etwa soll
          dieses Jahr <b>2,5 Millionen Franken</b> erhalten. In den beiden
          Folgejahren wird der Betrag auf 1,6 respektive 0,7 Millionen Franken
          reduziert.
        </Textbox>

        <Textbox value={15} align='right'>
          Für <b>Selbstständige</b> wurden <b>15 Millionen Franken</b>{' '}
          reserviert. Finanziert wird dieser Beitrag über die Jubiläumsdividende
          der Zürcher Kantonalbank.
        </Textbox>

        <Textbox value={8} align='left'>
          <b>8 Millionen Franken</b> gehen an den <b>Sportfonds</b> für dessen
          gemeinnützige Organisationen. Am meisten Geld erhalten jedoch die
          Spitäler:
        </Textbox>

        <Textbox value={135} align='left'>
          <b>135 Millionen Franken</b> erhalten Zürichs <b>Krankenhäuser</b>,
          zusätzlich können sie Kredite beantragen (dazu später mehr). Mit
          dieser direkten Hilfe sollen nicht nur die Corona-bedingten
          Mehraufwände gedeckt, sondern auch das vom Bund auferlegte
          Operationsverbot kompensiert werden. Das Hilfspaket ist jedoch noch
          nicht im Trockenen, der Kantonsrat wird erst noch darüber entscheiden.
          Die Spitäler wehren sich, der Betrag sei zu gering.
          <Image
            file={require('images/notaufnahme.jpg')}
            source='Zusätzliche Plätze in der Notaufnahme wie hier im Inselspital Bern drücken Spitälern aufs Budget. (Adrian Moser)'
          />
        </Textbox>
        {/* <Link href='https://www.tagesanzeiger.ch/kanton-zuerich-schnuert-300-millionen-paket-fuer-spitaeler-671586332986'>ist der Betrag zu klein</Link> */}

        <Textbox value={36.37} align='right'>
          <p>
            <b>20 Millionen Franken</b> gehen an die <b>Fachstelle Kultur</b>,
            finanziert über den Lotteriefonds. Da über den Lotteriefonds nur
            Non-Profit-Organisationen unterstützt werden können, werden
            kommerzielle Kulturunternehmen mit zusätzlichen, maximalen{' '}
            <b>13,5 Millionen Franken</b> unterstützt. 1083 Gesuche seien gemäss
            Regierungsrätin Jacqueline Fehr eingegangen. 185 wurden bisher
            behandelt.
          </p>
          <p>
            Nebst den grossen Finanzposten verschlingt das Coronavirus auch
            diverse kleinere Beträge. Die Zürcher <b>Maskenmaschine</b> etwa
            wird Zürich ungefähr <b>800’000 Franken</b> kosten.{' '}
            <b>570’000 Franken</b> hat der Kanton bisher durch{' '}
            <b>Mietzinserlasse</b> auf kantonalen Immobilien verloren (Stand 12.
            Juni). Die <b>Kantonsapotheke</b> hat kostenloses Material im Wert
            von <b>1,5 Millionen Franken</b> abgegeben. Auch für Kinderkrippen
            stehen noch Ausgaben an. Über die Höhe kann die Bildungsdirektion
            noch keine Aussage machen.
          </p>
          <Image
            file={require('images/maskenmaschine.jpg')}
            source='Die Zürcher Maskenmaschine. Noch ist sie nicht in Betrieb. (Samuel Schalch)'
          />
        </Textbox>

        {/* -- AUSFÄLLE -- */}

        <Separator pos={238} title='Ausfälle'>
          Hilfspakete haben einen klaren Kostenrahmen. Anders sieht es mit
          Mindereinnahmen aus. Und diese können gehörig auf die Bilanz drücken.
          Genaue Zahlen werden erst in Monaten oder Jahren verfügbar sein.
          Studien geben jedoch bereits jetzt Hinweise, was Zürichs Steuerzahler
          erwarten dürfen.
        </Separator>

        <Textbox value={72.95} align='left'>
          <p>
            Bereits bekannt ist der <b>Dividendenverzicht des Flughafens</b>.
            218 Millionen Franken zahlt das Unternehmen seinen Eignern nun nicht
            aus. Damit entgehen dem Kanton, der ein Drittel der Aktien besitzt,{' '}
            <b>72,7 Millionen Franken</b>.
          </p>
          <Image
            file={require('images/flughafen.jpg')}
            source='Die Einnahmen im Flugverkehr sind eingebrochen, der Flughafen Zürich behält seine Dividende zurück. (Urs Jaudas)'
          />
          <p>
            Auch auf der Strasse nahm der Verkehr ab und damit auch die
            Einnahmen dank <b>Autobahnraststätten</b>. An diesen verdient der
            Kanton jeweils mit. Die zuständige Direktion schätzt den Rückgang
            auf <b>200’000–300’000 Franken</b>. Ebenfalls wird der kantonale
            Anteil an den <b>Schwerverkehrsabgaben</b>, welche Lastwagen
            entrichten müssen, geringer ausfallen. Zahlen sind noch nicht
            bekannt.
          </p>
          <p>
            Richtig einschenken – und den grössten Schwankungen unterworfen –
            wird der Rückgang der Kantonssteuern. Die Dimensionen: gewaltig.
          </p>
        </Textbox>

        <Textbox value={910.4} align='right'>
          <p>
            Geschlossene Restaurants, Auftragsstornierungen, Kurzarbeit – die
            Wirtschaft hat massiv gelitten. Das wird sich auch auf der{' '}
            <b>Steuerrechnung 2020</b> zeigen. Die Konjunkturforschungsstelle
            (KOF) der ETH Zürich rechnet schweizweit mit einem Rückgang der
            Einkommenssteuer für natürliche Personen um 10 Prozent. Die
            Gewinnsteuer für Unternehmen könnte gar um die Hälfte zurückgehen.
            Auf Anfrage teilt die KOF mit, dass sie auch für den Kanton Zürich
            mit dieser Grössenordnung rechnet.
          </p>
          <p>
            In Zahlen ausgedrückt:{' '}
            <b>910 Millionen Franken weniger Steuereinnahmen</b>. Zum Vergleich:
            Im letzten Jahr Betrugen die Steuereinnahmen etwa 7,8 Milliarden
            Franken.
          </p>
          <p>
            Die Schätzungen der KOF sind grob, der Streubereich gross. Nach dem
            besten Szenario gerechnet, gehen die Steuern «nur» um etwa 580
            Millionen Franken zurück. Im Worst Case könnten 1,2 Milliarden in
            Zürichs Finanzen fehlen.
          </p>
          <p>
            Immerhin fällt dieser Betrag nicht auf einmal an, denn die
            provisorische Steuerrechnung für dieses Jahr wurde anhand des
            Corona-freien letzten Jahres berechnet. Damit erhält der Kanton
            vorerst zu viele Steuern, die er nach und nach zurückzahlen kann.
          </p>
        </Textbox>

        {/* -- GARANTIEN -- */}

        <Separator pos={1220} title='Garantien'>
          Der Kantonsrat behandelte das Coronavirus nicht nur mit sofortigen
          Geldspritzen, er erleichterte Unternehmen auch den Zugang zu Krediten.
          Zwar geben nach wie vor Banken das Geld aus, der Bund wie auch der
          Kanton bürgen aber für allfällige Verluste. Diese Garantien belasten
          das Kantonsbudget nur, wenn Unternehmen zahlungsunfähig werden.
        </Separator>

        <Textbox value={170} align='right'>
          <b>Spitäler</b> etwa sollen nebst sofortigen Finanzmitteln auch
          leichter zu Geld kommen. Dafür deckt der Kanton Darlehen über{' '}
          <b>170 Millionen Franken</b>.
        </Textbox>

        <Textbox value={425} align='right'>
          Auch <b>Unternehmen</b> sollen schnelles Geld erhalten, dafür wurde
          eine Kreditausfallgarantie über <b>425 Millionen</b> gesprochen,
          ergänzend zu der Kreditgarantie des Bundes. Bis jetzt (Stand 17. Juni)
          wurden Kredite über 33,5 Millionen Franken bezogen. Ob der Kanton
          bereits seiner Bürgpflicht nachkommen musste, ist nicht bekannt.
        </Textbox>
      </Timeline>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div>
            <Image
              file={require('images/schaad.jpg')}
              source='Zusätzliche Plätze in der Notaufnahme wie hier im Inselspital Bern drücken Spitälern aufs Budget. (Adrian Moser)'
            />
            Die verschiedenen Hilfspakete summieren sich zwar zu einem
            ordentlichen Betrag, am meisten belasten wird Zürichs Finanzen
            jedoch der Rückgang der Steuern. Bekannt wird dieser erst im
            nächsten Jahr. Nicht berücksichtigt in dieser Übersicht ist die
            Kurzarbeitsentschädigung. Diese organisiert zwar der Kanton,
            finanziert werden die Entschädigungen jedoch über den
            Bundeshaushalt.
          </div>
        </div>
      </div>

      <Footer
        articleId={ARTICLE_ID}
        hidePortfolioTeaser
        hideUserFeedback
        credits={[['Text & Entwicklung', 'Simon Huwiler']]}
      />
    </div>
  )
}
